import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Truck, Zap, CalendarX } from 'lucide-react';

const DeliveryCountdown = ({ openingHour = 21, closingHour = 2 }) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [isDeliveryDay, setIsDeliveryDay] = useState(false);
  const [nextDeliveryDay, setNextDeliveryDay] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const currentDay = now.getDay(); // 0 = Dimanche, 5 = Vendredi, 6 = Samedi
      const currentHour = now.getHours();
      
      // Jours de livraison : Vendredi (5) et Samedi (6)
      const isFriday = currentDay === 5;
      const isSaturday = currentDay === 6;
      const isSundayEarly = currentDay === 0 && currentHour < closingHour; // Dimanche avant 2h (suite du samedi)
      
      // Vérifier si c'est un jour de livraison
      const deliveryDay = isFriday || isSaturday || isSundayEarly;
      setIsDeliveryDay(deliveryDay);
      
      // Calculer le prochain jour de livraison si on n'est pas un jour de livraison
      if (!deliveryDay) {
        const daysUntilFriday = (5 - currentDay + 7) % 7 || 7;
        const nextFriday = new Date(now);
        nextFriday.setDate(now.getDate() + daysUntilFriday);
        setNextDeliveryDay(nextFriday.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }));
        return null;
      }
      
      // Vérifier si on est dans les heures d'ouverture
      let isCurrentlyOpen = false;
      
      if (isFriday || isSaturday) {
        // Vendredi ou Samedi : ouvert de 21h à minuit
        isCurrentlyOpen = currentHour >= openingHour;
      }
      if ((isSaturday && currentHour < closingHour) || isSundayEarly) {
        // Samedi avant 2h (suite du vendredi) ou Dimanche avant 2h (suite du samedi)
        isCurrentlyOpen = true;
      }
      
      setIsOpen(isCurrentlyOpen);
      
      if (isCurrentlyOpen) {
        return null; // Pas besoin de countdown
      }
      
      // Calculer le temps jusqu'à 21h
      let targetDate = new Date(now);
      targetDate.setHours(openingHour, 0, 0, 0);
      
      // Si on est avant 21h le vendredi ou samedi
      const diff = targetDate - now;
      
      if (diff <= 0) {
        return { hours: 0, minutes: 0, seconds: 0 };
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return { hours, minutes, seconds };
    };

    const timer = setInterval(() => {
      const time = calculateTimeLeft();
      if (time) {
        setTimeLeft(time);
      }
    }, 1000);

    // Initial calculation
    const initialTime = calculateTimeLeft();
    if (initialTime) {
      setTimeLeft(initialTime);
    }

    return () => clearInterval(timer);
  }, [openingHour, closingHour]);

  if (!mounted) return null;

  // Si ce n'est pas un jour de livraison
  if (!isDeliveryDay) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative overflow-hidden rounded-2xl p-6 mb-6"
        style={{
          background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.2) 0%, rgba(10, 0, 30, 0.95) 50%, rgba(75, 85, 99, 0.15) 100%)',
          boxShadow: '0 0 30px rgba(107, 114, 128, 0.3)',
          border: '2px solid rgba(107, 114, 128, 0.4)'
        }}
      >
        <div className="relative z-10 text-center">
          <CalendarX className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-white mb-2">
            🚫 Livraisons fermées
          </h3>
          <p className="text-gray-400 mb-3">
            Les livraisons sont disponibles uniquement le <span className="text-cyan-400 font-semibold">Vendredi</span> et <span className="text-cyan-400 font-semibold">Samedi</span> de 21h à 2h
          </p>
          <div className="bg-white/5 rounded-lg p-3 inline-block">
            <p className="text-sm text-gray-300">
              📅 Prochaine ouverture : <span className="text-cyan-400 font-semibold capitalize">{nextDeliveryDay}</span>
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // Si c'est ouvert, afficher un message différent
  if (isOpen) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative overflow-hidden rounded-2xl p-6 mb-6"
        style={{
          background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)',
          boxShadow: '0 0 40px rgba(16, 185, 129, 0.5)'
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + (i % 2) * 40}%`
              }}
              animate={{
                y: [-10, 10, -10],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{
                duration: 2 + i * 0.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
        
        <div className="relative z-10 flex items-center justify-center gap-4">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
          >
            <Truck className="w-10 h-10 text-white" />
          </motion.div>
          <div className="text-center">
            <h3 className="text-2xl font-bold text-white">🚀 Livraisons ouvertes !</h3>
            <p className="text-green-100">Commandez maintenant jusqu'à 2h du matin</p>
          </div>
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            <Zap className="w-8 h-8 text-yellow-300" />
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Compte à rebours
  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <motion.div
        key={value}
        initial={{ scale: 1.2, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative"
      >
        <div 
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex items-center justify-center text-3xl sm:text-4xl font-bold text-white"
          style={{
            background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.2) 0%, rgba(99, 102, 241, 0.3) 100%)',
            boxShadow: '0 0 20px rgba(0, 255, 255, 0.3), inset 0 0 20px rgba(0, 0, 0, 0.3)',
            border: '2px solid rgba(0, 255, 255, 0.4)'
          }}
        >
          {String(value).padStart(2, '0')}
        </div>
        <motion.div
          className="absolute inset-0 rounded-xl"
          style={{
            background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1) 0%, transparent 50%)',
          }}
          animate={{
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>
      <span className="text-cyan-300 text-sm mt-2 font-medium">{label}</span>
    </div>
  );

  const Separator = () => (
    <div className="flex flex-col items-center justify-center gap-2 px-1">
      <motion.div
        className="w-2 h-2 rounded-full bg-cyan-400"
        animate={{ opacity: [1, 0.3, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
      />
      <motion.div
        className="w-2 h-2 rounded-full bg-cyan-400"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1, repeat: Infinity }}
      />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl p-6 mb-6"
      style={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(10, 0, 30, 0.95) 50%, rgba(0, 255, 255, 0.15) 100%)',
        boxShadow: '0 0 40px rgba(99, 102, 241, 0.3), inset 0 0 60px rgba(0, 0, 0, 0.5)',
        border: '2px solid rgba(99, 102, 241, 0.4)'
      }}
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
            animate={{
              y: [-20, 20],
              x: [-10, 10],
              opacity: [0, 0.5, 0]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      {/* Glowing ring effect */}
      <motion.div
        className="absolute inset-0 rounded-2xl"
        style={{
          background: 'radial-gradient(circle at center, transparent 30%, rgba(99, 102, 241, 0.1) 70%)',
        }}
        animate={{
          scale: [1, 1.02, 1],
          opacity: [0.5, 0.8, 0.5]
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-6">
          <motion.div
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-3"
          >
            <Clock className="w-10 h-10 text-cyan-400" />
          </motion.div>
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">
            🛵 Livraisons à <span className="text-cyan-400">21h</span>
          </h3>
          <p className="text-gray-400 text-sm">Préparez votre commande !</p>
        </div>

        {/* Countdown */}
        <div className="flex items-center justify-center gap-2 sm:gap-4">
          <TimeBlock value={timeLeft.hours} label="Heures" />
          <Separator />
          <TimeBlock value={timeLeft.minutes} label="Minutes" />
          <Separator />
          <TimeBlock value={timeLeft.seconds} label="Secondes" />
        </div>

        {/* Footer message */}
        <motion.p
          className="text-center text-gray-400 text-sm mt-6"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          ⚡ Soyez prêt, ça arrive vite !
        </motion.p>
      </div>
    </motion.div>
  );
};

export default DeliveryCountdown;
